import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  MAP_DATA_SERVICE_DATA_SET,
  CFCProps,
  MapPlaceSuggestion,
  MapSearchLocationHistoryObject,
  MapPlaceSuggestionCustomFields
} from 'types';
import { useDispatch, connect } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { setMapSearchLocationHistory } from 'redux/app/actions';
import {
  getPlaceCandidateAddressStart,
  getGeoscapeAddressStart,
  clearGeoscapeAddress,
  clearPlaceCandidateAddress,
  clearPlaceSuggest
} from 'redux/map/actions';
import SearchInput from 'components/MapLocationSearchV2/addressSearch';
import ResultsBox from 'components/MapLocationSearchV2/resultsBox';
import {
  optimizeLabel,
  constructArcGISWorldGeocodingLocality
} from 'components/MapLocationSearchV2/helper';
import MapSettings from 'components/Map/settings';
import { useEventListener } from 'utilities/hooks.util';

const searchMapZoomLevel = MapSettings.zoomLevel;

const MapLocationSearch: React.FC<Props> = ({
  onClickResult,
  suggestion,
  user,
  mapSearchLocationHistory,
  geoscapeAddress,
  addressCandidate
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isShowResult, setIsShowResult] = useState<boolean>(false);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [
    userMapLocationSearchHistory,
    seUserMapLocationSearchHistory
  ] = useState<MapSearchLocationHistoryObject[]>([]);

  const onClickCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const center = [position.coords.longitude, position.coords.latitude];
      setSearchInputValue('');
      onClickResult({
        center,
        zoom: searchMapZoomLevel + 1
      });
    });
    setIsShowResult(false);
  };

  const saveMapSearchHistory = useCallback(
    (item: MapPlaceSuggestion) => {
      const searchHistories = mapSearchLocationHistory || {};
      const { Value } = item;

      const searchHistoryOtherScope = userMapLocationSearchHistory.filter(
        (sh: any) => sh.scope !== MAP_DATA_SERVICE_DATA_SET.GNAF
      );
      let newSearchHistorySameScope = userMapLocationSearchHistory.filter(
        (sh: any) =>
          sh.Value !== Value && sh.scope === MAP_DATA_SERVICE_DATA_SET.GNAF
      );
      const newHistory = { scope: MAP_DATA_SERVICE_DATA_SET.GNAF, ...item };
      newSearchHistorySameScope = [newHistory, ...newSearchHistorySameScope];
      if (newSearchHistorySameScope.length > 5) {
        newSearchHistorySameScope.pop();
      }
      const newSearchHistory = [
        ...newSearchHistorySameScope,
        ...searchHistoryOtherScope
      ];
      searchHistories[user.id] = newSearchHistory;
      seUserMapLocationSearchHistory(newSearchHistory);
      dispatch(setMapSearchLocationHistory(searchHistories));
    },
    [userMapLocationSearchHistory, mapSearchLocationHistory, user.id, dispatch]
  );

  const onItemClick = (item: any) => {
    if (item.AddressId) {
      dispatch(
        getGeoscapeAddressStart({
          selectedSuggestion: item
        })
      );
    } else if (item.Value && item.Coordinate && item.CustomFields) {
      user?.id && saveMapSearchHistory(item);
      const center = item.Coordinate.split(',')?.map((c: string) => Number(c));
      onClickResult({
        center,
        zoom: item.Extent ? searchMapZoomLevel + 1 : searchMapZoomLevel
      });
      setSearchInputValue(
        optimizeLabel(
          MAP_DATA_SERVICE_DATA_SET.GNAF,
          item.Value,
          item.CustomFields,
          !!item.Extent,
          !!item.Extent
        )
      );
    } else if (item.MagicKey && item.Value) {
      dispatch(
        getPlaceCandidateAddressStart({
          query: item.Value,
          outFields: '*',
          magicKey: item.MagicKey
        })
      );
    }

    dispatch(clearPlaceSuggest());
    setIsShowResult(false);
  };

  const processAddressCandidate = useCallback(() => {
    const addrCandidate = addressCandidate?.[0];

    if (addrCandidate) {
      const addrCandidateAttr = addrCandidate.attributes;
      const CustomFields: MapPlaceSuggestionCustomFields = {
        locality:
          addrCandidateAttr.Addr_type === 'Locality'
            ? `${constructArcGISWorldGeocodingLocality(addrCandidateAttr)}`
            : '',
        region: addrCandidate.attributes.Region || ''
      };

      user?.id &&
        saveMapSearchHistory({
          Coordinate: `${addrCandidate.location.x}, ${addrCandidate.location.y}`,
          CustomFields,
          Highlight: addrCandidate.address,
          Score: addrCandidate.score.toString(),
          Value: addrCandidate.address,
          Extent: addrCandidate.extent
        });
      const center = [addrCandidate.location.x, addrCandidate.location.y];
      onClickResult({
        center,
        zoom: searchMapZoomLevel + 1
      });
      setSearchInputValue(
        optimizeLabel(
          MAP_DATA_SERVICE_DATA_SET.GNAF,
          addrCandidate.address,
          CustomFields,
          true,
          true
        )
      );
    }

    dispatch(clearPlaceCandidateAddress());
  }, [addressCandidate, user, onClickResult, saveMapSearchHistory, dispatch]);

  const processGeoscapeAddress = useCallback(() => {
    if (
      geoscapeAddress &&
      geoscapeAddress.coordinate &&
      geoscapeAddress.selectedSuggestion
    ) {
      user?.id && saveMapSearchHistory(geoscapeAddress.selectedSuggestion);
      const center = geoscapeAddress.coordinate
        .split(',')
        ?.map((c: string) => Number(c));
      onClickResult({
        center,
        zoom: searchMapZoomLevel + 1
      });
      setSearchInputValue(
        optimizeLabel(
          MAP_DATA_SERVICE_DATA_SET.GNAF,
          geoscapeAddress.selectedSuggestion.Value,
          geoscapeAddress.selectedSuggestion.CustomFields,
          false,
          false
        )
      );
    }

    dispatch(clearGeoscapeAddress());
  }, [geoscapeAddress, user, saveMapSearchHistory, onClickResult, dispatch]);

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          setIsShowResult(false);
        }
      }
    },
    [containerRef]
  );

  useEffect(() => {
    user?.id &&
      mapSearchLocationHistory?.[user.id] &&
      seUserMapLocationSearchHistory(mapSearchLocationHistory[user.id]);
  }, [user, mapSearchLocationHistory]);

  useEffect(() => {
    if (geoscapeAddress) {
      processGeoscapeAddress();
    }
  }, [geoscapeAddress, processGeoscapeAddress]);

  useEffect(() => {
    if (addressCandidate) {
      processAddressCandidate();
    }
  }, [addressCandidate, processAddressCandidate]);

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <div className="history-page-location-search-container" ref={containerRef}>
      <label className="bx--label">Zoom to location</label>
      <SearchInput
        showHideBox={() => setIsShowResult(true)}
        placeholder="Address search"
        noOpenButton={true}
        currentScope={MAP_DATA_SERVICE_DATA_SET.GNAF}
        searchResult={searchInputValue}
      />
      {isShowResult && (
        <div className="mls-item-container">
          {isShowResult && (
            <ResultsBox
              scope={MAP_DATA_SERVICE_DATA_SET.GNAF}
              userSearchHistory={userMapLocationSearchHistory}
              items={suggestion}
              onItemClick={onItemClick}
              onClickCurrentLocation={onClickCurrentLocation}
            />
          )}
        </div>
      )}
    </div>
  );
};

type ownProps = {
  onClickResult: Function;
};

const mapStateToProps = ({ app, mapCache, session }: RootState) => ({
  mapSearchLocationHistory: app.mapSearchLocationHistory,
  user: session.currentUser,
  suggestion: mapCache.suggestion,
  geoscapeAddress: mapCache.geoscapeAddress,
  addressCandidate: mapCache.addressCandidate
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

export default connector(MapLocationSearch);
